/*
 * Copyright (c) 2024 SARL SIGMA POLYNESIA
 */

import { Accordion, Box, Button, Group, Stack, Table, Text, Title } from '@mantine/core'
import { DateTime } from 'luxon'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons/faInfoCircle'
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons/faExclamationTriangle'
import Amount from '../transaction/Amount'
import { faDownload } from '@fortawesome/free-solid-svg-icons/faDownload'
import LabelledValue from '../LabelledValue'
import CustomerLink from '../customer/CustomerLink'
import AnchorLink from '../../../../client/js/components/AnchorLink'
import { getLeaseLink } from '../../lib/links/leaseLinks'
import { FormattedMessage, useIntl } from 'react-intl'
import ManagementReportItem from './ManagementReportItem'
import ManagementReportBalance from './ManagementReportBalance'
import { useQuery } from '@tanstack/react-query'
import { downloadManagementReport } from '../../lib/rest/managementReportAPIClient'
import LeaseInfoHoverCard from '../lease/LeaseInfoHoverCard'

export type ManagementReportPeriodProps = {
  customerId: number;
  estateId: number;
  isManagementReport: boolean;
  onDeleteItem (item: ManagementReportItem): void;
  period: ManagementReportPeriod;
}

function ManagementReportPeriod (props: ManagementReportPeriodProps) {
  const {
    customerId,
    estateId,
    isManagementReport,
    onDeleteItem,
    period
  } = props

  const intl = useIntl()

  const { items, leases, month, year } = period
  const manualCount = items.filter((el) => !el.generated).length
  const unpaidCount = items.filter((el) => !el.paid).length

  const reportFileQuery = useQuery({
    enabled: false,
    queryKey: ['downloadManagementReport', customerId, estateId, year, month],
    queryFn: () => downloadManagementReport({ customerId, estateId, year, month })
  })

  return (
    <Accordion.Item value={period.period}>
      <Accordion.Control>
        <Group>
          <Title order={2} tt="capitalize">
            {DateTime.fromObject({ year, month }).toLocaleString({
              year: 'numeric',
              month: 'long'
            })}
          </Title>
          <Group ml="auto">
            {manualCount > 0
              ? <Text>
                <FontAwesomeIcon fixedWidth icon={faInfoCircle} />
              </Text>
              : null}

            {unpaidCount > 0
              ? <Text c="red">
                <FontAwesomeIcon fixedWidth icon={faExclamationTriangle} />
              </Text>
              : null}

            <Amount size="lg" fw="bold" value={period.balance} colored />

            {isManagementReport
              ? <Button
                disabled={reportFileQuery.isFetching}
                loading={reportFileQuery.isFetching}
                size="xs"
                variant="default"
                onClick={(ev) => {
                  ev.stopPropagation()
                  reportFileQuery.refetch()
                }}
              >
                <FontAwesomeIcon fixedWidth icon={faDownload} />
              </Button>
              : null}
          </Group>
        </Group>
      </Accordion.Control>
      <Accordion.Panel>
        <Stack gap="xs">
          {leases.map((lease) => (
            <Group key={lease.id}>
              <Box fz="sm">
                <Group>
                  {isManagementReport
                    ? <LabelledValue
                      label={intl.formatMessage({
                        id: 'tenant',
                        defaultMessage: 'Locataire'
                      })}
                      value={lease.Tenant ? <CustomerLink customer={lease.Tenant} /> : null}
                    />
                    : <LabelledValue
                      label={intl.formatMessage({
                        id: 'lessor',
                        defaultMessage: 'Bailleur'
                      })}
                      value={lease.Lessor ? <CustomerLink customer={lease.Lessor} /> : null}
                    />
                  }
                  <LabelledValue
                    label={intl.formatMessage({
                      id: 'lease',
                      defaultMessage: 'Dossier de location'
                    })}
                    value={<>
                      <LeaseInfoHoverCard leaseId={lease.id}>
                        <span>
                          <AnchorLink to={getLeaseLink(lease.id)}>
                            {lease.reference}
                          </AnchorLink>
                        </span>
                      </LeaseInfoHoverCard>
                      &nbsp;
                      {`(${intl.formatDate(lease.startDate)}${lease.endDate
                        ? ` - ${intl.formatDate(lease.endDate)}`
                        : ''})`}
                    </>}
                  />
                </Group>
              </Box>
            </Group>
          ))}

          <Table
            my={0}
            highlightOnHover
          >
            <Table.Thead>
              <Table.Tr>
                <Table.Th w={100}>
                  <FormattedMessage
                    id="date"
                    defaultMessage="Date"
                  />
                </Table.Th>
                <Table.Th w={150}>
                  <FormattedMessage
                    id="invoice"
                    defaultMessage="Facture"
                  />
                </Table.Th>
                <Table.Th>
                  <FormattedMessage
                    id="label"
                    defaultMessage="Libellé"
                  />
                </Table.Th>
                <Table.Th
                  ta="end"
                  w={120}
                >
                  <FormattedMessage
                    id="revenues_including_taxes"
                    defaultMessage="Revenus TTC"
                  />
                </Table.Th>
                <Table.Th
                  ta="end"
                  w={120}
                >
                  <FormattedMessage
                    id="expenses_including_taxes"
                    defaultMessage="Dépenses TTC"
                  />
                </Table.Th>
                <Table.Th
                  ta="end"
                  w={120}
                >
                  <FormattedMessage
                    id="including_vat"
                    defaultMessage="Dont TVA"
                  />
                </Table.Th>
                <Table.Th
                  ta="end"
                  w={120}
                >
                  <FormattedMessage
                    id="including_cps"
                    defaultMessage="Dont CPS"
                  />
                </Table.Th>
              </Table.Tr>
            </Table.Thead>

            <Table.Tbody>
              {items.map((item) => (
                <ManagementReportItem
                  key={item.id}
                  item={item}
                  onDelete={onDeleteItem}
                />
              ))}
            </Table.Tbody>

            <ManagementReportBalance data={period} bg="gray.0" />
          </Table>
        </Stack>
      </Accordion.Panel>
    </Accordion.Item>
  )
}

export default ManagementReportPeriod
